import React, { useCallback } from 'react';
import Icon, { IconKey } from 'flow/components/ui-components/Icon';
import Tooltip from 'flow/components/ui-components/Tooltip';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    muteParticipant,
    removeParticipant,
    turnOffParticipantVideo,
} from 'flow/actions/meeting-actions';
import {
    selectViewerCanRemoveParticipants,
    selectViewerIsInternal,
} from 'flow/selectors/meeting-selectors';

interface TileParticipantControlsProps {
    feedName: string;
    audioActive: boolean;
    videoActive: boolean;
}

interface ControlButtonProps {
    iconKey: IconKey;
    label: string;
    onClick: () => void;
    testId: string;
}

const ControlButton = ({
    iconKey,
    label,
    onClick,
    testId,
}: ControlButtonProps) => {
    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            // Don't let the click bubble to the tile underneath.
            event.stopPropagation();
            onClick();
        },
        [onClick],
    );

    return (
        <Tooltip label={label} placement="top">
            <button
                type="button"
                className="hover:darken-10 flex items-center justify-center rounded-full p-2 text-white hover:cursor-pointer"
                onClick={handleClick}
                aria-label={label}
                data-test-id={testId}
            >
                <Icon iconKey={iconKey} size="1.25rem" />
            </button>
        </Tooltip>
    );
};

/**
 * Centered control bar shown on hover over a remote participant's tile
 * (web/desktop) — VID-53/VID-311 host controls. Holds "mute" (while their mic
 * is on), "turn off camera" (while their camera is on), and "remove" (gated
 * per context — see selectViewerCanRemoveParticipants). All three are
 * internal-colleague-only (AC-1716): external guests get no host-control
 * actions on other participants at all, not just a disabled/empty bar.
 */
const TileParticipantControls: React.FC<TileParticipantControlsProps> = ({
    feedName,
    audioActive,
    videoActive,
}) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const canRemove = useAppSelector(selectViewerCanRemoveParticipants);
    const viewerIsInternal = useAppSelector(selectViewerIsInternal);

    const mute = useCallback(
        () => dispatch(muteParticipant(feedName)),
        [dispatch, feedName],
    );
    const turnOffCamera = useCallback(
        () => dispatch(turnOffParticipantVideo(feedName)),
        [dispatch, feedName],
    );
    const remove = useCallback(
        () => dispatch(removeParticipant(feedName)),
        [dispatch, feedName],
    );

    if (!viewerIsInternal) {
        return null;
    }

    // Full-tile overlay is click-through (pointer-events-none) so only the pill
    // itself is interactive and the rest of the tile stays hoverable. The pill
    // is transparent, not invisible, so Tab can reach its buttons, and it shows
    // as soon as the tile holds keyboard focus: a keyboard user never lands on
    // a control they cannot see. While transparent it also takes no tap. The
    // row more-options menu is the other keyboard path.
    return (
        <div className="pointer-events-none absolute inset-0 z-[12] flex items-center justify-center">
            <div className="reveal-on-hover-or-focus flex items-center gap-1 rounded-full bg-gray-900/80 px-2 py-1.5 transition-opacity">
                {audioActive && (
                    <ControlButton
                        iconKey="mic_outlined"
                        label={i18next.t('videoconference.host_controls.mute')}
                        onClick={mute}
                        testId="meeting-mute-participant"
                    />
                )}
                {videoActive && (
                    <ControlButton
                        iconKey="video"
                        label={i18next.t(
                            'videoconference.host_controls.turn_off_camera',
                        )}
                        onClick={turnOffCamera}
                        testId="meeting-turn-off-participant-video"
                    />
                )}
                {canRemove && (
                    <ControlButton
                        iconKey="agent_remove"
                        label={i18next.t(
                            'videoconference.host_controls.remove',
                        )}
                        onClick={remove}
                        testId="meeting-remove-participant"
                    />
                )}
            </div>
        </div>
    );
};

export default TileParticipantControls;
